import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import HelpIcon from '@mui/icons-material/Help';
import ReplayIcon from '@mui/icons-material/Replay';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { ContentBlockType } from '@onesource/schemas';
import { useGetAttempt, useGetLesson, useGetUserAttempts, type TQuizAnswerWithQuestion } from '@onesource/services';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { MdChevronLeft } from 'react-icons/md';

/**
 * Quiz Results Route
 * Path: /community-hub/spaces/:id/modules/:moduleId/lessons/:lessonId/results/:attemptId
 */
export const Route = createFileRoute(
  '/_protected/community-hub/spaces/$id/modules/$moduleId/lessons/$lessonId/results/$attemptId',
)({
  component: QuizResultsRoute,
});

/**
 * Format seconds to MM:SS or HH:MM:SS format
 */
const formatTime = (seconds: number): string => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Answer Review Card Component
 */
const AnswerReviewCard = ({
  answer,
  questionNumber,
  showCorrectAnswers,
}: {
  answer: TQuizAnswerWithQuestion;
  questionNumber: number;
  showCorrectAnswers: boolean;
}) => {
  const { question } = answer;
  const options = question.options || [];

  const getSelectedOptions = () => {
    if (answer.selectedOptionIds && answer.selectedOptionIds.length > 0) {
      return options.filter(opt => answer.selectedOptionIds.includes(opt.id)).map(opt => opt.optionText);
    }
    return [];
  };

  const getCorrectOptions = () => {
    return options.filter(opt => opt.isCorrect).map(opt => opt.optionText);
  };

  const getOrderingDisplay = (sequence: string[]) => {
    return sequence.map(id => options.find(opt => opt.id === id)?.optionText || 'Unknown').join(' → ');
  };

  const isCorrect = answer.isCorrect;
  const isPending = answer.isCorrect === null;

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: 'white',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: '2px solid',
        borderColor: isPending ? '#ffa726' : isCorrect ? '#4caf50' : '#ef5350',
      }}
    >
      {/* Question Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box flex={1}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>
            <Box component="span" sx={{ color: '#ff4d6d' }}>
              Question {questionNumber}
            </Box>
          </Typography>
          <Typography variant="body1">{question.questionText}</Typography>
        </Box>
        <Box sx={{ ml: 2 }}>
          {isPending ? (
            <HelpIcon sx={{ color: '#ffa726', fontSize: 28 }} />
          ) : isCorrect ? (
            <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 28 }} />
          ) : (
            <CancelIcon sx={{ color: '#ef5350', fontSize: 28 }} />
          )}
        </Box>
      </Box>

      {/* Answer Details */}
      <Box sx={{ pl: 2 }}>
        {/* User's Answer */}
        <Box mb={1}>
          <Typography variant="body2" color="text.secondary" component="span">
            Your answer:{' '}
          </Typography>
          <Typography variant="body2" component="span" fontWeight={500}>
            {question.questionType === 'short_answer' && <>{answer.textAnswer || '(No answer)'}</>}
            {question.questionType === 'ordering' && answer.orderingSequence && (
              <>{getOrderingDisplay(answer.orderingSequence) || '(No answer)'}</>
            )}
            {['multiple_choice', 'multiple_select', 'true_false'].includes(question.questionType) && (
              <>{getSelectedOptions().join(', ') || '(No answer)'}</>
            )}
          </Typography>
        </Box>

        {/* Correct Answer */}
        {showCorrectAnswers && question.questionType !== 'short_answer' && (
          <Box mb={1}>
            <Typography variant="body2" color="text.secondary" component="span">
              Correct answer:{' '}
            </Typography>
            <Typography variant="body2" component="span" sx={{ color: '#4caf50', fontWeight: 500 }}>
              {question.questionType === 'ordering' ? (
                <>
                  {options
                    .sort((a, b) => a.position - b.position)
                    .map(opt => opt.optionText)
                    .join(' → ')}
                </>
              ) : (
                <>{getCorrectOptions().join(', ')}</>
              )}
            </Typography>
          </Box>
        )}

        {/* Explanation */}
        {showCorrectAnswers && question.explanation && (
          <Box
            mt={2}
            p={1.5}
            sx={{
              backgroundColor: 'rgba(255, 77, 109, 0.05)',
              borderRadius: 1,
              borderLeft: '3px solid #ff4d6d',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              <strong>Explanation:</strong> {question.explanation}
            </Typography>
          </Box>
        )}

        {/* Manual Grading Feedback */}
        {answer.feedback && (
          <Box
            mt={2}
            p={1.5}
            sx={{
              backgroundColor: 'rgba(33, 150, 243, 0.1)',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2">
              <strong>Feedback:</strong> {answer.feedback}
            </Typography>
          </Box>
        )}

        {/* Pending Grading Notice */}
        {isPending && question.questionType === 'short_answer' && (
          <Alert severity="info" sx={{ mt: 1 }}>
            This answer requires manual grading and is pending review.
          </Alert>
        )}
      </Box>
    </Box>
  );
};

function QuizResultsRoute() {
  const { id: spaceId, moduleId, lessonId, attemptId } = Route.useParams();
  const navigate = useNavigate();

  // Fetch attempt data
  const { data: attempt, isLoading, error } = useGetAttempt(attemptId);

  // Fetch lesson to find the quiz content block
  const { data: lesson } = useGetLesson(lessonId);

  // Find the quiz content block ID
  const quizContentBlock = lesson?.contentBlocks?.find(
    block => block.blockType === ContentBlockType.QUIZ && block.quizId === attempt?.quizId,
  );

  // Fetch user attempts for this quiz to check if retry is allowed
  const { data: userAttempts } = useGetUserAttempts(attempt?.quizId || '');

  // Calculate if retry is allowed
  const attemptsUsed = userAttempts?.length || 0;
  const maxAttempts = attempt?.quiz?.maxAttempts;
  const canRetry = !maxAttempts || attemptsUsed < maxAttempts;

  // Back to lesson URL
  const backToLessonUrl = `/community-hub/spaces/${spaceId}/modules/${moduleId}/lessons/${lessonId}`;

  // Handle retry - navigate to the quiz route
  const handleRetry = () => {
    if (quizContentBlock) {
      navigate({
        to: '/community-hub/spaces/$id/modules/$moduleId/lessons/$lessonId/quiz/$contentId',
        params: { id: spaceId, moduleId, lessonId, contentId: quizContentBlock.id },
      });
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #FFF5F5 0%, #FFFFFF 100%)',
          p: 3,
        }}
      >
        <Box maxWidth="800px" mx="auto">
          <Alert severity="error">Failed to load results: {error.message}</Alert>
          <Button
            startIcon={<MdChevronLeft size={20} />}
            onClick={() => navigate({ to: backToLessonUrl })}
            sx={{
              mt: 2,
              bgcolor: '#ff4d6d',
              color: 'white',
              '&:hover': { bgcolor: '#e63956' },
            }}
          >
            Back to Lesson
          </Button>
        </Box>
      </Box>
    );
  }

  // No attempt found
  if (!attempt) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #FFF5F5 0%, #FFFFFF 100%)',
          p: 3,
        }}
      >
        <Box maxWidth="800px" mx="auto">
          <Alert severity="warning">Attempt not found</Alert>
          <Button
            startIcon={<MdChevronLeft size={20} />}
            onClick={() => navigate({ to: backToLessonUrl })}
            sx={{
              mt: 2,
              bgcolor: '#ff4d6d',
              color: 'white',
              '&:hover': { bgcolor: '#e63956' },
            }}
          >
            Back to Lesson
          </Button>
        </Box>
      </Box>
    );
  }

  const showCorrectAnswers = attempt.quiz?.showCorrectAnswers ?? true;
  const sortedAnswers = [...(attempt.answers || [])].sort((a, b) => a.question.position - b.question.position);
  const scorePercent = attempt.scorePercent !== null ? Math.round(attempt.scorePercent) : 0;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #FFF5F5 0%, #FFFFFF 100%)',
        p: 3,
      }}
    >
      <Box maxWidth="800px" mx="auto">
        {/* Progress/Score Bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 6 }}>
          <Typography sx={{ fontWeight: 500, minWidth: 40 }}>{scorePercent}%</Typography>
          <LinearProgress
            variant="determinate"
            value={scorePercent}
            sx={{
              flex: 1,
              height: 8,
              borderRadius: 4,
              bgcolor: 'rgba(0,0,0,0.08)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                bgcolor: attempt.passed ? '#4caf50' : '#ff4d6d',
              },
            }}
          />
        </Box>

        {/* Result Title - Centered */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          {/* Pass/Fail Icon */}
          <Box mb={2}>
            {attempt.passed ? (
              <EmojiEventsIcon sx={{ fontSize: 64, color: '#4caf50' }} />
            ) : attempt.passed === false ? (
              <CancelIcon sx={{ fontSize: 64, color: '#ef5350' }} />
            ) : (
              <HelpIcon sx={{ fontSize: 64, color: '#ffa726' }} />
            )}
          </Box>

          <Typography
            variant="h4"
            sx={{
              fontFamily: 'Georgia, serif',
              fontWeight: 400,
              mb: 2,
            }}
          >
            {attempt.passed
              ? 'Congratulations! You Passed!'
              : attempt.passed === false
                ? 'Not Quite - Keep Practicing!'
                : 'Results Pending'}
          </Typography>

          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {attempt.quiz?.title}
          </Typography>

          {/* Score Stats */}
          <Box display="flex" justifyContent="center" gap={4}>
            <Box>
              <Typography variant="h3" fontWeight="bold" sx={{ color: attempt.passed ? '#4caf50' : '#ff4d6d' }}>
                {attempt.scorePercent !== null ? `${Math.round(attempt.scorePercent)}%` : '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Score
              </Typography>
            </Box>

            <Divider orientation="vertical" flexItem />

            <Box>
              <Typography variant="h3" fontWeight="bold">
                {attempt.earnedPoints ?? '-'}/{attempt.totalPoints ?? '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Points
              </Typography>
            </Box>

            {attempt.timeSpentSeconds && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                    <AccessTimeIcon sx={{ fontSize: 28 }} />
                    <Typography variant="h4" fontWeight="bold">
                      {formatTime(attempt.timeSpentSeconds)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Time Spent
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {/* Status Badges */}
          <Box display="flex" justifyContent="center" gap={1} mt={3}>
            <Chip
              label={`Attempt ${attempt.attemptNumber}${maxAttempts ? ` of ${maxAttempts}` : ''}`}
              variant="outlined"
            />
            <Chip
              label={attempt.status.charAt(0).toUpperCase() + attempt.status.slice(1)}
              color={attempt.status === 'graded' ? 'success' : attempt.status === 'submitted' ? 'warning' : 'default'}
            />
          </Box>
        </Box>

        {/* Answer Review Section */}
        <Box mb={4}>
          <Typography
            variant="h5"
            sx={{
              fontFamily: 'Georgia, serif',
              mb: 3,
              textAlign: 'center',
            }}
          >
            Answer Review
          </Typography>
          {!showCorrectAnswers && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Correct answers are not shown for this quiz.
            </Alert>
          )}
          <Box display="flex" flexDirection="column" gap={2}>
            {sortedAnswers.map((answer, index) => (
              <AnswerReviewCard
                key={answer.id}
                answer={answer}
                questionNumber={index + 1}
                showCorrectAnswers={showCorrectAnswers}
              />
            ))}
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Button
            startIcon={<MdChevronLeft size={20} />}
            onClick={() => navigate({ to: backToLessonUrl })}
            sx={{
              bgcolor: '#ff4d6d',
              color: 'white',
              px: 3,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': { bgcolor: '#e63956' },
            }}
          >
            Back to Lesson
          </Button>

          {canRetry && quizContentBlock && (
            <Button
              endIcon={<ReplayIcon />}
              onClick={handleRetry}
              sx={{
                bgcolor: '#4caf50',
                color: 'white',
                px: 3,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': { bgcolor: '#388e3c' },
              }}
            >
              Try Again
            </Button>
          )}
        </Box>

        {/* Max Attempts Notice */}
        {!canRetry && (
          <Alert severity="info" sx={{ mt: 3 }}>
            You have used all {maxAttempts} attempts for this quiz.
          </Alert>
        )}
      </Box>
    </Box>
  );
}
